import { Content, Flex, Link, Text } from '@adobe/react-spectrum';
import { useBreakpoint } from '@react-spectrum/utils';
import React, { useContext } from 'react';
import { Helmet } from 'react-helmet';
import { Link as RouterLink } from 'react-router-dom';
import styled from 'styled-components';

import { LinkBanner } from '@exchange-frontends/components';
import { useAnalyticsPageLoad, useRootHref } from '@exchange-frontends/custom-hooks';
import { featurePage, handleHomeForLaunch } from '@exchange-frontends/utils';

import blueBgPNG from '../../../../static/illustrations/Illu_AdobeExchange_CreativityAndDesign_600x224@2x.png';
import blueBgWEBP from '../../../../static/illustrations/Illu_AdobeExchange_CreativityAndDesign_600x224@2x.webp';
import redBgPNG from '../../../../static/illustrations/Illu_AdobeExchange_DC_395x240@2x.png';
import redBgWEBP from '../../../../static/illustrations/Illu_AdobeExchange_DC_395x240@2x.webp';
import heroImagePNG from '../../../../static/illustrations/Illu_AdobeExchange_ExploreAdobeApp_1376x256@2x.png';
import heroImageWEBP from '../../../../static/illustrations/Illu_AdobeExchange_ExploreAdobeApp_1376x256@2x.webp';
import purpleBgPNG from '../../../../static/illustrations/Illu_AdobeExchange_MarketingAndDocument_600x224@2x.png';
import purpleBgWEBP from '../../../../static/illustrations/Illu_AdobeExchange_MarketingAndDocument_600x224@2x.webp';

import { useFeaturedApps } from '../utils/hooks';
import { ConfigContext } from './App';
import FeaturedAppsGrid from './FeaturedAppsGrid';
import FeaturedProducts from './FeaturedProducts';
import HeroImage from './HeroImage';

// Styled Components
/**
 * main home container
 * @memberof Home
 * @type {styled-component}
 */
const HomeContainer = styled.div`
  margin: 0 auto;
  max-width: 1376px;
  min-height: 100vh;
`;

/**
 * Home/Featured component
 * @component
 * @category Featured
 * @prop {object} props - Props pased to the component
 * @returns JSX.Element
 */
const Home = (props) => {
  const { ROUTES } = useContext(ConfigContext) ?? {};
  const { matchedBreakpoints } = useBreakpoint();
  const localImages = {
    heroImage: {
      webp: heroImageWEBP,
      png: heroImagePNG,
    },
    purpleBackground: {
      webp: purpleBgWEBP,
      png: purpleBgPNG,
    },
    blueBackground: {
      webp: blueBgWEBP,
      png: blueBgPNG,
    },
    redBackground: {
      webp: redBgWEBP,
      png: redBgPNG,
    },
  };
  const ecBrowseLink = useRootHref(ROUTES?.BROWSE + '/ec' ?? '');
  const ccBrowseLink = useRootHref(ROUTES?.BROWSE + '/cc' ?? '');

  const { loading: ecLoading, encounteredError: ecIsError, apps: ecApps } = useFeaturedApps('EC');
  const { loading: dcLoading, encounteredError: dcIsError, apps: dcApps } = useFeaturedApps('DC');

  useAnalyticsPageLoad(featurePage);

  return (
    <HomeContainer onClickCapture={handleHomeForLaunch}>
      <Helmet>
        <title>Adobe Exchange</title>
        <meta name="robots" content="noodp, FOLLOW, INDEX" />
        <meta name="title" content={`Adobe Exchange`} />
        <meta property="og:title" content={`Adobe Exchange`} />
        <meta
          name="description"
          content="Explore the Adobe App Marketplaces: Thousands of apps to enhance your experience with products across all Adobe Clouds."
        />
        <meta
          property="og:description"
          content="Explore the Adobe App Marketplaces: Thousands of apps to enhance your experience with products across all Adobe Clouds."
        />
        <meta property="og:type" content="website" />
        <meta name="keywords" content="" />
      </Helmet>
      <HeroImage
        imgUrl={localImages.heroImage}
        title="Explore the Adobe App Marketplaces"
        subtitle="Thousands of apps to enhance your experience with products across all Adobe Clouds."
      />
      <Content
        data-testid="featured-content"
        marginX={!matchedBreakpoints.includes('XL') ? '0' : 'size-1000'}
        marginY="size-500"
      >
        <Flex gap="size-200" marginY="size-500" direction={matchedBreakpoints.includes('L') ? 'row' : 'column'}>
          <LinkBanner
            bgImgUrl={localImages.blueBackground}
            title="Creative Cloud"
            subtitle="Explore extensions that expand your creativity."
            buttonText="Discover the Creative Cloud marketplace"
            buttonHref={ccBrowseLink}
          />
          <LinkBanner
            bgImgUrl={localImages.redBackground}
            title="Acrobat Sign"
            subtitle="Speed up every transaction with e-signatures."
            buttonText="Discover the Acrobat Sign marketplace"
            buttonHref={`${ecBrowseLink}?product=SIGN&sort=RELEVANCE`}
          />
          <LinkBanner
            bgImgUrl={localImages.purpleBackground}
            title="Experience Cloud"
            subtitle="Explore the Experience Cloud marketplace."
            buttonText="Discover the Experience Cloud marketplace"
            buttonHref={ecBrowseLink}
          />
        </Flex>
        <FeaturedAppsGrid
          title="Platinum apps for Experience Cloud"
          analyticsId="Featured apps for Experience Cloud"
          viewAllAriaLabel="View all apps for Experience Cloud"
          loading={ecLoading}
          encounteredError={ecIsError}
          apps={ecApps ?? Array(6).fill(null)}
          viewAllHref={`${ecBrowseLink}?product=All`}
          errorMessage={
            <>
              <Text>There are no Platinum Experience Cloud apps currently. </Text>
              <Link variant="secondary">
                <RouterLink to="apps/browse/ec">Explore all Experience Cloud apps.</RouterLink>
              </Link>
            </>
          }
        />
        <FeaturedProducts />
        {/*
        <Carousel marginBottom="size-500">
          {carouselItems.map((item, idx) => (
            <CarouselItem key={item.title + idx}>
              <LinkBanner
                title={item.title}
                subtitle={item.subtitle}
                buttonText={item.buttonText}
                onButtonPress={() => {}}
                bgSpectrumColor={item.bgSpectrumColor}
                additionalImg={item.additionalImg}
                bgImgUrl={item.bgImgUrl}
                logoImgUrl={item.logoImgUrl}
              />
            </CarouselItem>
          ))}
        </Carousel>
        */}
        <FeaturedAppsGrid
          title="Featured integrations for Acrobat Sign"
          viewAllAriaLabel="View all integrations for Acrobat Sign"
          loading={dcLoading}
          encounteredError={dcIsError}
          apps={dcApps ?? Array(6).fill(null)}
          viewAllHref={`${ecBrowseLink}?product=SIGN&sort=RELEVANCE`}
          errorMessage={
            <>
              <Text>There are no featured Acrobat Sign integrations currently. </Text>
              <Link variant="secondary">
                <RouterLink to="apps/browse/ec?product=SIGN">Explore all Acrobat Sign integrations.</RouterLink>
              </Link>
            </>
          }
        />
        {/* <LinkBanner
          title="Do more with Creative Cloud"
          subtitle="Explore Creative Cloud plugins on the Adobe Stock Marketplace."
          buttonText="Go to Adobe Stock"
          onButtonPress={() => {}}
          bgSpectrumColor="indigo-500"
          additionalImg={localImages.handBagsIcon}
          marginTop="40px"
        /> */}
      </Content>
    </HomeContainer>
  );
};

export default Home;
